import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css'; // We'll update this CSS
import axios from 'axios';

export default function KnowledgePage({ url }) {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('Latest First');
  const [blogData, setBlogData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [displayedBlogs, setDisplayedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize] = useState(9);
  const [currentPage, setCurrentPage] = useState(1);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  // Transform blog data
  const transformBlogData = (item) => {
    const keywords = item.blog_keyword ? item.blog_keyword.split(',') : [];
    const primaryKeyword = keywords.length > 0 ? keywords[0].trim() : "General";

    // Map keywords to color schemes - enhanced with more variations
    const getBadgeColor = (keyword) => {
      const colorMap = {
        "Tax": "success",
        "Investment": "primary",
        "Finance": "info",
        "Insurance": "warning",
        "Section 80C": "danger",
        "Income Tax": "danger",
        "GST": "indigo",
        "Banking": "cyan",
        "Compliance": "teal",
        "Corporate": "purple"
      };

      // Check if any keywords match our color map
      for (const key in colorMap) {
        if (keyword && keyword.includes && keyword.includes(key)) {
          return colorMap[key];
        }
      }

      // Default color based on first letter (for variety)
      const firstChar = keyword && keyword.charAt ? keyword.charAt(0).toLowerCase() : 'a';
      const charCode = firstChar.charCodeAt(0);
      const colors = ["primary", "success", "info", "warning", "danger", "secondary"];
      return colors[charCode % colors.length];
    };

    return {
      id: item.blog_id,
      slug: item.blog_slug,
      image: item.blog_image,
      category: primaryKeyword,
      badgeColor: getBadgeColor(primaryKeyword),
      title: item.blog_name || '',
      excerpt: item.blog_description || '',
      author: "Admin", // or assign dynamically if you store authors
      date: new Date(item.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      created_at: new Date(item.created_at),
      keywords: keywords,
      readTime: Math.max(3, Math.ceil((item.blog_description?.length || 0) / 1000)) // Estimate read time
    };
  };

  // Fetch blog data
  useEffect(() => {
    const getKnowledgeData = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(`${url}/blogs`);

        if (res.status === 200) {
          const transformedData = res.data.map(transformBlogData);

          // Sort by latest first (default)
          const sortedData = [...transformedData].sort((a, b) =>
            b.created_at - a.created_at
          );

          // Set featured post to the most recent item
          if (sortedData.length > 0) {
            setFeaturedPost(sortedData[0]);
            setBlogData(sortedData.slice(1)); // Rest of the blogs
          } else {
            setBlogData(sortedData);
          }
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setError("Unable to load blog posts. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    getKnowledgeData();
  }, [url]);

  // Apply filters and search
  useEffect(() => {
    let result = [...blogData];

    if (activeFilter !== 'All') {
      result = result.filter(blog => {
        if (!blog.title) return false;
        const words = blog.title
          .toLowerCase()
          .split(/\s+/)
          .map(word => word.replace(/[^\w]/g, ''));

        return words.includes(activeFilter.toLowerCase());
      });
    }

    // Apply search with null checks
    if (searchTerm && searchTerm.trim && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();

      result = result.filter(blog =>
        (blog.title && blog.title.toLowerCase && blog.title.toLowerCase().includes(term)) ||
        (blog.excerpt && blog.excerpt.toLowerCase && blog.excerpt.toLowerCase().includes(term))
      );
    }

    // Apply sorting
    if (sortBy === 'Latest First') {
      result.sort((a, b) => b.created_at - a.created_at);
    } else if (sortBy === 'Oldest First') {
      result.sort((a, b) => a.created_at - b.created_at);
    }

    setFilteredData(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [blogData, activeFilter, searchTerm, sortBy]);

  // Update displayed blogs
  useEffect(() => {
    const end = currentPage * pageSize;
    const displayItems = filteredData.slice(0, end);
    setDisplayedBlogs(displayItems);
    setHasMore(end < filteredData.length);
  }, [filteredData, currentPage, pageSize]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleSearchButtonClick = () => {
    // Already applied via useEffect
    console.log(`Searching for: ${searchTerm}`);
  };

  const handleLoadMore = () => {
    if (hasMore) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  // Placeholder image URL
  const placeholderImage = "https://via.placeholder.com/800x600";

  // Extract unique categories for filter options
  const getUniqueCategories = () => {
    const wordCount = {};

    blogData.forEach(blog => {
      if (blog.title) {
        const words = blog.title
          .toLowerCase()
          .split(/\s+/)
          .map(word => word.replace(/[^\w]/g, '')) // Remove punctuation
          .filter(word =>
            word.length > 2 &&
            !['the', 'and', 'for', 'with', 'this', 'that', 'from', 'into', 'your', 'you', 'are', 'has', 'was', 'have', 'will', 'not'].includes(word)
          );

        words.forEach(word => {
          if (word) {
            wordCount[word] = (wordCount[word] || 0) + 1;
          }
        });
      }
    });

    // Convert to array, sort by frequency, take top 8
    const topWords = Object.entries(wordCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(entry => entry[0]);

    return ['All', ...topWords];
  };

  const filterCategories = getUniqueCategories();

  // Loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-grow text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading knowledge articles...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger p-4 shadow-sm">
          <h4 className="alert-heading mb-3">Oops! Something went wrong</h4>
          <p>{error}</p>
          <button 
            className="btn btn-outline-danger mt-3"
            onClick={() => window.location.reload()}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="knowledge-page">
      {/* Enhanced Header with Gradient Background */}
      <header className="header-gradient position-relative overflow-hidden">
        <div className="position-absolute top-0 start-0 p-3 z-10">
          <img
            src="logo.png"
            alt="LegalFinTax Logo"
            className="logo-image"
            height={60}
            width={60}
          />
        </div>
        
        <div className="container py-5 position-relative" style={{ zIndex: 10 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-8 col-md-10">
              <h1 className="display-4 fw-bold mb-3 text-white animated-title">
                Knowledge Hub
              </h1>
              <p className="lead text-white mb-4 subtitle-text">
                Explore our expert articles on taxes, financial compliance, investments, and more.
              </p>

              {/* Enhanced Search Bar */}
              <div className="search-container mx-auto">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Search for tax guides, financial tips..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                    className="form-control search-input"
                    aria-label="Search knowledge base"
                  />
                  <button
                    onClick={handleSearchButtonClick}
                    className="btn btn-light search-button"
                    type="button"
                  >
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="wave-separator">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="#f8f9fa" fillOpacity="1" d="M0,128L48,138.7C96,149,192,171,288,170.7C384,171,480,149,576,149.3C672,149,768,171,864,176C960,181,1056,171,1152,154.7C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="bg-light py-5 main-content">
        <div className="container">
          {/* Filter and Sort Controls */}
          <div className="filter-controls mb-5">
            <div className="row align-items-center g-3">
              <div className="col-md-8">
                <h5 className="filter-heading text-uppercase fw-bold mb-3">
                  <i className="bi bi-funnel me-2"></i>Browse by Topic
                </h5>
                <div className="filter-buttons">
                  {filterCategories.map(filter => (
                    <button
                      key={filter}
                      onClick={() => handleFilterClick(filter)}
                      className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="sort-control d-flex justify-content-md-end align-items-center">
                  <label className="me-2 text-muted d-none d-md-block">Sort:</label>
                  <select
                    value={sortBy}
                    onChange={handleSortChange}
                    className="form-select sort-select"
                    aria-label="Sort blog posts"
                  >
                    <option>Latest First</option>
                    <option>Oldest First</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Post (Only show when filter is "All") */}
          {activeFilter === "All" && featuredPost && (
            <div className="featured-post mb-5 animate-in">
              <div className="card border-0 overflow-hidden featured-card">
                <div className="row g-0">
                  <div className="col-lg-6 featured-image-container">
                    <img
                      src={featuredPost.image ? `https://www.legalfintax.com/storage/blogimages/${featuredPost.image}` : placeholderImage}
                      alt={featuredPost.title}
                      className="featured-image"
                    />
                    <div className="featured-badge">
                      <span className="badge featured-label">
                        <i className="bi bi-star-fill me-1"></i> Featured
                      </span>
                    </div>
                  </div>
                  
                  <div className="col-lg-6">
                    <div className="card-body featured-content p-4 p-lg-5">
                      <div className="mb-3">
                        <span className={`category-badge ${featuredPost.badgeColor}`}>
                          {featuredPost.category}
                        </span>
                        
                        {featuredPost.keywords && featuredPost.keywords.slice(0, 2).map((keyword, idx) => (
                          keyword !== featuredPost.category &&
                          <span key={idx} className="keyword-badge">
                            {keyword}
                          </span>
                        ))}
                      </div>
                      
                      <h2 className="featured-title">
                        {featuredPost.title}
                      </h2>
                      
                      <p className="featured-excerpt">
                        {featuredPost.excerpt}
                      </p>
                      
                      <div className="featured-meta">
                        <span className="date-badge">
                          <i className="bi bi-calendar me-1"></i> {featuredPost.date}
                        </span>
                        <span className="read-time">
                          <i className="bi bi-clock me-1"></i> {featuredPost.readTime} min read
                        </span>
                      </div>
                      
                      <a href={`/knowledge/${featuredPost.slug}`} className="btn btn-primary read-more-btn mt-4">
                        Read Full Article <i className="bi bi-arrow-right ms-1"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Blog Posts Grid */}
          {displayedBlogs.length > 0 ? (
            <div className="row g-4 blog-grid">
              {displayedBlogs.map((post, index) => (
                <div key={post.id || index} className="col-md-6 col-lg-4 blog-card-wrapper">
                  <div className="card h-100 blog-card">
                    <div className="card-image-container">
                      <img
                        src={post.image ? `https://www.legalfintax.com/storage/blogimages/${post.image}` : placeholderImage}
                        className="card-img-top blog-image"
                        alt={post.title}
                        loading="lazy"
                      />
                      <span className={`category-flag ${post.badgeColor}`}>
                        {post.category}
                      </span>
                    </div>
                    
                    <div className="card-body blog-content">
                      <div className="keyword-container mb-2">
                        {post.keywords && post.keywords.slice(0, 3).map((keyword, idx) => (
                          keyword !== post.category &&
                          <span key={idx} className="keyword-pill">
                            {keyword}
                          </span>
                        ))}
                      </div>
                      
                      <h3 className="blog-title">
                        {post.title}
                      </h3>
                      
                      <p className="blog-excerpt">
                        {post.excerpt && post.excerpt.length > 100
                          ? `${post.excerpt.substring(0, 100)}...`
                          : post.excerpt}
                      </p>
                      
                      <div className="blog-meta">
                        <span className="meta-date">
                          <i className="bi bi-calendar3 me-1"></i> {post.date}
                        </span>
                        <span className="meta-read">
                          <i className="bi bi-book me-1"></i> {post.readTime} min
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-footer blog-footer">
                      <a href={`/knowledge/${post.slug}`} className="read-link">
                        Read Article <i className="bi bi-arrow-right ms-1"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <div className="text-center py-5">
                <div className="no-results-icon mb-3">
                  <i className="bi bi-search"></i>
                </div>
                <h3>No matching articles found</h3>
                <p className="text-muted">Try adjusting your search terms or filters</p>
                <button
                  onClick={() => {
                    setActiveFilter('All');
                    setSearchTerm('');
                  }}
                  className="btn btn-outline-primary mt-3">
                  <i className="bi bi-grid me-2"></i>View All Articles
                </button>
              </div>
            </div>
          )}

          {/* Load More Button */}
          {hasMore && displayedBlogs.length > 0 && (
            <div className="text-center mt-5 load-more-container">
              <button
                onClick={handleLoadMore}
                className="btn btn-lg load-more-btn"
              >
                Load More Articles <i className="bi bi-chevron-down ms-1"></i>
              </button>
            </div>
          )}
        </div>
      </main>

     

      {/* Enhanced Footer */}
      <footer className="bg-dark text-white pt-5 pb-4">
        <div className="container">
          <div className="row g-4">
            {/* Company Info */}
            <div className="col-lg-4 mb-4">
              <div className="d-flex align-items-center mb-3">
                <img src="logo.png" height={50} width={50} alt="LegalFinTax Logo" className="me-2" />
                <h2 className="m-0 fw-bold">LegalFinTax</h2>
              </div>
              
              <p className="mb-3 footer-about">
                Providing expert financial and tax solutions to simplify your financial journey. 
                Trust our expertise for all your taxation and compliance needs.
              </p>
              
              <div className="social-icons mt-3">
                <a href="https://www.facebook.com/legalfintax" className="social-icon" aria-label="Facebook">
                  <i className="bi bi-facebook"></i>
                </a>
                <a href="https://twitter.com" className="social-icon" aria-label="Twitter">
                  <i className="bi bi-twitter"></i>
                </a>
                <a href="https://www.linkedin.com/company/legalfintax" className="social-icon" aria-label="LinkedIn">
                  <i className="bi bi-linkedin"></i>
                </a>
                <a href="https://www.instagram.com/legal.fin.tax/" className="social-icon" aria-label="Instagram">
                  <i className="bi bi-instagram"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-lg-2 col-md-4 mb-4">
              <h5 className="footer-heading">Quick Links</h5>
              <ul className="footer-links">
                <li><a href="https://legalfintax.com/">Home</a></li>
                <li><a href="https://legalfintax.com/contact-us">Contact</a></li>
                <li><a href="https://rupela.in/">Accounting Software</a></li>
              </ul>
            </div>

            {/* Services */}
            <div className="col-lg-3 col-md-4 mb-4">
              <h5 className="footer-heading">Our Services</h5>
              <ul className="footer-links">
                <li><a href="https://www.legalfintax.com/income-tax-return">Income Tax Return</a></li>
                <li><a href="https://www.legalfintax.com/gst-registration">GST Registration</a></li>
                <li><a href="https://www.legalfintax.com/trademark-registration">Trademark Registration</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="col-lg-3 col-md-4 mb-4">
              <h5 className="footer-heading">Contact Us</h5>
              <ul className="footer-links contact-info">
                <li>
                  <i className="bi bi-geo-alt-fill me-2"></i>
                  A-71, Karni Nagar, Pawanpuri<br />Bikaner, Rajasthan (334003), India
                </li>
                <li>
                  <i className="bi bi-telephone-fill me-2"></i>
                  <a href="tel:+919079398040">+91 9079398040</a>
                </li>
                <li>
                  <i className="bi bi-envelope-fill me-2"></i>
                  <a href="mailto:info@legalfintax.com">info@legalfintax.com</a>
                </li>
               
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="row mt-4 pt-3 border-top border-secondary">
            <div className="col-md-6 text-center text-md-start">
              <p className="small mb-0">© {new Date().getFullYear()} LegalFinTax. All rights reserved.</p>
            </div>
            <div className="col-md-6 text-center text-md-end">
              <a href="https://www.legalfintax.com/privacy-policy" className="footer-policy-link">Privacy Policy</a>
              <a href="https://www.legalfintax.com/terms-and-conditions" className="footer-policy-link">Terms of Service</a>
              <a href="https://www.legalfintax.com/refund-policy" className="footer-policy-link">Refund Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}